import { Phone } from "../../components/mobile/phone-shell";
import { JourneyWelcomeScreen } from "./journey-welcome";
import { JourneyPickSkillScreen } from "./journey-pick-skill";
import { JourneyVaultSetupScreen } from "./journey-vault-setup";
import { JourneyProcessingScreen } from "./journey-processing";
import { JourneyDashboardScreen } from "./journey-dashboard";
import { JourneyChatScreen } from "./journey-chat";
import { JourneyTrackerRecommendScreen } from "./journey-tracker-recommend";
import { JourneyHomeWithTrackerScreen } from "./journey-home-tracker";
import { JourneyNotificationsScreen } from "./journey-notifications";
import { JourneyNextMorningScreen } from "./journey-next-morning";
import { JourneyMarketplaceBrowseScreen } from "./journey-marketplace-browse";
import { JourneySkillDetailScreen } from "./journey-skill-detail";
import { JourneyInstallDataCheckScreen } from "./journey-install-data-check";
import { alpha, borderAlpha } from "../../components/mobile/primitives";

export const JOURNEY_PHASES = [
  {
    phase: "Onboarding",
    color: "var(--color-kin-green-mid)",
    steps: [
      {
        num: "01",
        title: "Welcome & Register",
        desc: "Sign in with Google. No health data yet — just a person arriving.",
        emotion: "Curious",
        screen: null,
        phoneContent: <JourneyWelcomeScreen />,
        hideTabBar: true,
      },
      {
        num: "02",
        title: "Choose Your First Skill",
        desc: "Pick one skill to start fast. You can add more skills later, create your own skill, or skip now and use a standard starter skill.",
        emotion: "Intentional",
        screen: "skills",
        phoneContent: <JourneyPickSkillScreen />,
        hideTabBar: true,
        phoneScrollable: true,
      },
      {
        num: "03",
        title: "Set Up Your Vault",
        desc: "Connect only what this first skill needs to run. One required source is enough for first value; the rest can be added later.",
        emotion: "Invested",
        screen: "vault",
        phoneContent: <JourneyVaultSetupScreen />,
        hideTabBar: true,
        phoneScrollable: true,
      },
      {
        num: "04",
        title: "Processing",
        desc: "Kin organizes, indexes, and validates your data transparently. You can leave this screen and come back when your first dashboard is ready.",
        emotion: "Anticipation",
        screen: "analysis",
        phoneContent: <JourneyProcessingScreen />,
        hideTabBar: true,
      },
    ],
  },
  {
    phase: "First Value",
    color: "var(--color-kin-lavender)",
    steps: [
      {
        num: "05",
        title: "Open Your First Dashboard",
        desc: "Step five is immediate value: your chosen skill dashboard. You see the main takeaway first, then KPIs and trend detail. No detour screen.",
        emotion: "Wow",
        screen: "insight",
        phoneContent: <JourneyDashboardScreen />,
        phoneTab: "skills",
        phoneScrollable: true,
      },
      {
        num: "06",
        title: "Chat With Your Skill",
        desc: "Ask follow-up questions directly from the dashboard context. The conversation stays evidence-grounded and points to concrete next actions.",
        emotion: "Trust",
        screen: "chat",
        phoneContent: <JourneyChatScreen />,
        phoneTab: "chat",
      },
    ],
  },
  {
    phase: "Engagement Loop",
    color: "var(--color-kin-orange)",
    steps: [
      {
        num: "07",
        title: "AI Recommends a Tracker",
        desc: "Mid-conversation, the AI suggests a targeted tracker with clear burden and payoff: about 30 seconds per meal for 7 days to improve explanation confidence.",
        emotion: "Makes sense",
        screen: "chat",
        phoneContent: <JourneyTrackerRecommendScreen />,
        phoneTab: "chat",
      },
      {
        num: "08",
        title: "Activate the Tracker",
        desc: "One tap activates it, and the Today screen changes instantly. Personalized home state confirms this is your workflow, not a static template.",
        emotion: "Ownership",
        screen: "home",
        phoneContent: <JourneyHomeWithTrackerScreen />,
        phoneTab: "home",
      },
      {
        num: "09",
        title: "Enable Notifications",
        desc: "Permission is requested only after the first successful log. The reminder ask is contextual, specific, and clearly optional.",
        emotion: "Willing",
        screen: null,
        phoneContent: <JourneyNotificationsScreen />,
        phoneTab: "home",
      },
    ],
  },
  {
    phase: "Day 2",
    color: "var(--color-kin-gold-dark)",
    steps: [
      {
        num: "10",
        title: "Next Morning",
        desc: "Next morning, the app closes the loop: a prompt to log again plus an insight that explicitly uses yesterday's behavior. Cause and effect are visible.",
        emotion: "Habit forming",
        screen: "home",
        phoneContent: <JourneyNextMorningScreen />,
        phoneTab: "home",
        phoneScrollable: true,
      },
    ],
  },
  {
    phase: "New Skill",
    color: "var(--color-kin-pink)",
    steps: [
      {
        num: "11",
        title: "Browse the Marketplace",
        desc: "A returning user opens the marketplace to discover new skills. Category filters, ratings, and install counts help them find the right fit fast.",
        emotion: "Exploring",
        screen: "skills",
        phoneContent: <JourneyMarketplaceBrowseScreen />,
        phoneTab: "skills",
        phoneScrollable: true,
      },
      {
        num: "12",
        title: "Skill Detail",
        desc: "The skill page shows what it analyzes, what data it needs, and who created it. One tap to install — no setup wizard, just a clear commitment.",
        emotion: "Informed",
        screen: "skills",
        phoneContent: <JourneySkillDetailScreen />,
        phoneTab: "skills",
        phoneScrollable: true,
      },
      {
        num: "13",
        title: "Data Check",
        desc: "Post-install, the app audits existing vault data against the skill's requirements. Green checks for what's already there, orange for missing with upload/connect actions. Same screen as onboarding vault setup.",
        emotion: "Confident",
        screen: "skills",
        phoneContent: <JourneyInstallDataCheckScreen />,
        phoneTab: "skills",
        phoneScrollable: true,
      },
      {
        num: "14",
        title: "Processing",
        desc: "The skill runs with a real-time step trace. User sees what the AI is doing: reading files, building dashboard. Same shared processing screen used during onboarding.",
        emotion: "Anticipation",
        screen: "analysis",
        phoneContent: <JourneyProcessingScreen />,
        hideTabBar: true,
      },
      {
        num: "15",
        title: "Dashboard",
        desc: "The payoff — again. Headline insight, KPIs, trend charts. The same dashboard pattern the user saw during onboarding, now for their new skill.",
        emotion: "Rewarded",
        screen: "insight",
        phoneContent: <JourneyDashboardScreen />,
        phoneTab: "skills",
        phoneScrollable: true,
      },
    ],
  },
];

export const _SCREEN_ICONS = {
  skills: (c) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  ),
  vault: (c) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
  analysis: (c) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  insight: (c) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 20h20" />
      <path d="M5 20V9l4-5 4 8 4-3 3 4v7" />
    </svg>
  ),
  chat: (c) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  home: (c) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
      <path d="M9 22V12h6v10" />
    </svg>
  ),
};

export function JourneyStepCard({ step, color }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        width: 270,
        flexShrink: 0,
      }}
    >
      {/* Phone mockup */}
      {step.phoneContent && (
        <Phone
          active={step.phoneTab || "home"}
          hideTabBar={step.hideTabBar}
          scrollable={step.phoneScrollable}
        >
          {step.phoneContent}
        </Phone>
      )}

      {/* Description card below phone */}
      <div
        style={{
          width: "100%",
          background: "rgba(254,253,251,0.60)",
          border: "1px solid rgba(255,255,255,0.50)",
          borderRadius: 16,
          padding: "12px 14px",
          backdropFilter: "blur(24px) saturate(150%)",
          boxShadow: "0 4px 20px -8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.7)",
        }}
      >
        {/* Step number + Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
                fontFamily: "'Manrope', system-ui",
              }}
            >
              {step.num}
            </span>
          </div>
          <p
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.2,
            }}
          >
            {step.title}
          </p>
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: 12,
            color: "var(--color-kin-muted)",
            lineHeight: 1.6,
            fontFamily: "'Manrope', system-ui",
            fontWeight: 400,
          }}
        >
          {step.desc}
        </p>

        {/* Emotion tag */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            marginTop: 8,
            padding: "3px 10px",
            borderRadius: 20,
            background: alpha(color, "12"),
            border: borderAlpha(color, "22"),
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: color,
            }}
          />
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color,
              fontFamily: "'Manrope', system-ui",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {step.emotion}
          </span>
        </div>
      </div>
    </div>
  );
}

export function JourneyDiagram() {
  return (
    <section
      className="px-6 sm:px-10 lg:px-16 pb-20"
      style={{ borderBottom: "1px solid rgba(108,108,112,0.10)" }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Section header */}
        <div style={{ marginBottom: 48 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "var(--color-kin-muted)",
              marginBottom: 14,
            }}
          >
            User Journey
          </p>
          <h2
            style={{
              fontSize: "clamp(28px, 4vw, 48px)",
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.05,
              marginBottom: 16,
            }}
          >
            From signup to
            <br />
            <span style={{ color: "var(--color-kin-green-soft)" }}>daily habit</span>
          </h2>
          <p
            style={{
              fontSize: 15,
              color: "var(--color-kin-muted)",
              lineHeight: 1.7,
              maxWidth: 520,
              fontWeight: 400,
            }}
          >
            One skill chosen. Data uploaded with purpose. Value delivered before any permission is
            asked. The loop closes the next morning.
          </p>
        </div>

        {/* Journey phases */}
        <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
          {JOURNEY_PHASES.map((phase) => (
            <div key={phase.phase}>
              {/* Phase header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    height: 1,
                    flex: 1,
                    maxWidth: 40,
                    background: `linear-gradient(to right, transparent, ${alpha(phase.color, "50")})`,
                  }}
                />
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 14px",
                    borderRadius: 20,
                    background: alpha(phase.color, "10"),
                    border: borderAlpha(phase.color, "28"),
                  }}
                >
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: phase.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: phase.color,
                      fontFamily: "'Manrope', system-ui",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    {phase.phase}
                  </span>
                </div>
                <div
                  style={{
                    height: 1,
                    flex: 1,
                    background: `linear-gradient(to right, ${alpha(phase.color, "50")}, transparent)`,
                  }}
                />
              </div>

              {/* Steps — wrapping grid of phones with descriptions below */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "40px 32px",
                  alignItems: "flex-start",
                  justifyContent: "center",
                }}
              >
                {phase.steps.map((step) => (
                  <JourneyStepCard key={step.num} step={step} color={phase.color} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Journey summary */}
        <div
          style={{
            marginTop: 48,
            padding: "24px 28px",
            background: "rgba(254,253,251,0.55)",
            border: "1px solid rgba(255,255,255,0.42)",
            borderRadius: 24,
            backdropFilter: "blur(20px)",
            boxShadow: "0 8px 32px -12px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
          }}
        >
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.20em",
              textTransform: "uppercase",
              color: "var(--color-kin-muted)",
              marginBottom: 12,
            }}
          >
            The Principle
          </p>
          <p
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.25,
              marginBottom: 12,
            }}
          >
            Earn every permission.
          </p>
          <p
            style={{
              fontSize: 14,
              color: "var(--color-kin-muted)",
              lineHeight: 1.7,
              fontWeight: 400,
              maxWidth: 540,
            }}
          >
            Data upload happens because a skill needs it. The vault concept is introduced through
            action, not explanation. Notifications are requested after value is proven. Each step
            earns the next.
          </p>

          {/* Phase color key */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
            {JOURNEY_PHASES.map((p) => (
              <div key={p.phase} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: p.color,
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--color-kin-muted)",
                    fontFamily: "'Manrope', system-ui",
                  }}
                >
                  {p.phase}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
